import { useRef } from 'react';
import { getCalendarUsageCount } from '@/features/interactions/addToGoogle/calendar.lib';
import { useRemoveIntegration } from '@/hooks/useRemoveIntegration';
import { IntegrationAccountKind, IntegrationType } from '@/types/gql.generated';
import {
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogCloseButton,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Link,
  Text,
} from '@/ui';
import { useDisconnectDialog } from './store';

export const DisconnectDialog = () => {
  const toast = useToast();
  const { removeIntegration, isPending } = useRemoveIntegration();
  const { isOpen, close, integration } = useDisconnectDialog();
  const cancelRef = useRef(null);

  const isOutlook = integration?.type === IntegrationType.OutlookCalendar;
  const providerName = isOutlook ? 'Outlook' : 'Google';

  // Microsoft manages third-party app access in different places depending on
  // the account type, which we record when the integration is connected. When
  // it's unknown — connected before we started capturing it, or the sign-in
  // carried no usable tenant claim — name both rather than guess.
  const getRevocation = (accountKind: IntegrationAccountKind | null) => {
    if (accountKind === IntegrationAccountKind.Personal) {
      return {
        href: 'https://account.microsoft.com/privacy',
        label: 'your Microsoft account privacy settings',
        suffix: '.',
      };
    }
    if (accountKind === IntegrationAccountKind.Organization) {
      // My Apps is the portal that actually exposes "Revoke Permissions" (My
      // Account does not). It only covers what the user consented to themselves
      // — anything an administrator granted for the organization has to be
      // revoked by that administrator, hence the second half of the sentence.
      return {
        href: 'https://myapps.microsoft.com/',
        label: 'the My Apps portal',
        suffix:
          ', or ask your administrator if they approved Agenda Hero for your organization.',
      };
    }
    return {
      href: 'https://account.microsoft.com/privacy',
      label: 'your Microsoft account privacy settings',
      suffix: ' (or myapps.microsoft.com for work or school accounts).',
    };
  };

  const revocation = getRevocation(integration?.accountKind ?? null);

  const handleRemove = () => {
    if (!integration) {
      return;
    }

    removeIntegration(integration.id, {
      onError: () =>
        toast.error(
          'Something went wrong while disconnecting this account. Please try again.'
        ),
      onSuccess: () => {
        close();
        toast.notify(
          `The ${integration.name} ${providerName} account has been disconnected from Agenda Hero. We will no longer be able to access calendars or events in this account`
        );
      },
    });
  };

  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={close}
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogCloseButton />
          <AlertDialogHeader>Disconnect Integration</AlertDialogHeader>
          <AlertDialogBody display="flex" flexDir="column" gap="4">
            <Text>
              Are you sure you want to remove the {providerName} integration{' '}
              <Text as="span" fontWeight="bold">
                {integration?.name}
              </Text>{' '}
              from your Agenda Hero account?
            </Text>
            {integration && getCalendarUsageCount(integration) > 0 && (
              <Text>
                <Text as="span" fontWeight="bold">
                  Warning:{' '}
                </Text>
                If you remove the integration, events from its {providerName}{' '}
                Calendars will no longer be available in any of your Agenda Hero
                schedules or planners.
              </Text>
            )}
            <Text color="customgray.mid" fontSize="sm">
              Disconnecting deletes the calendar data we imported from{' '}
              {providerName} and the access we stored.
              {isOutlook && (
                <>
                  {' '}
                  To also revoke Agenda Hero&apos;s access from Microsoft, visit{' '}
                  <Link href={revocation.href} isExternal textDecor="underline">
                    {revocation.label}
                  </Link>
                  {revocation.suffix}
                </>
              )}
            </Text>
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button
              colorScheme="dark"
              ref={cancelRef}
              variant="ghost"
              onClick={close}
            >
              Cancel
            </Button>
            <Button
              colorScheme="red"
              isLoading={isPending}
              ml={2}
              variant="ghost"
              onClick={handleRemove}
            >
              Disconnect
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};
